import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/shadcn/utils';

/**
 * The hiring-manager portal's modal: a small white sheet with a hairline
 * border, the portal's 15px title and 13px hint, and a footer for the
 * portal's small buttons. The recruiter dashboard keeps `dialog/Dialog`.
 */
export type PortalDialogProps = React.ComponentProps<
  typeof DialogPrimitive.Root
>;

function PortalDialog(props: PortalDialogProps) {
  return <DialogPrimitive.Root data-slot="portal-dialog" {...props} />;
}

function PortalDialogClose(
  props: React.ComponentProps<typeof DialogPrimitive.Close>,
) {
  return <DialogPrimitive.Close data-slot="portal-dialog-close" {...props} />;
}

interface PortalDialogContentProps extends React.ComponentProps<
  typeof DialogPrimitive.Content
> {
  /** Hide the corner close button, e.g. when the footer already offers Cancel. */
  showCloseButton?: boolean;
}

/**
 * Enter a little slower than exit: the eye follows the dialog in, while
 * closing answers a decision already made. A strong ease-out moves at once,
 * so the dialog reads as a reply to the click.
 */
const dialogMotion = cn(
  'ease-[cubic-bezier(0.23,1,0.32,1)]',
  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-200',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-120',
);

function PortalDialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: PortalDialogContentProps) {
  return (
    <DialogPrimitive.Portal data-slot="portal-dialog-portal">
      <DialogPrimitive.Overlay
        data-slot="portal-dialog-overlay"
        className={cn(dialogMotion, 'fixed inset-0 z-50 bg-neutral-900/30')}
      />
      <DialogPrimitive.Content
        data-slot="portal-dialog-content"
        className={cn(
          dialogMotion,
          // Scale is movement; with reduced motion only the fade remains.
          'motion-safe:data-[state=open]:zoom-in-[0.98] motion-safe:data-[state=closed]:zoom-out-[0.98]',
          // Pinned by its top edge, not centred: a centred dialog jumps when
          // its content grows (e.g. a validation error), moving the buttons
          // out from under the pointer. This one only grows downward.
          'fixed top-[min(20vh,10rem)] left-1/2 z-50 max-h-[calc(100dvh-min(20vh,10rem)-1rem)] -translate-x-1/2 overflow-y-auto',
          'bg-card flex w-full max-w-[calc(100%-2rem)] flex-col gap-5 rounded-xl border p-6 shadow-[0_8px_30px_rgba(0,0,0,0.06)] outline-none sm:max-w-[440px]',
          // Form errors fade in rather than popping into place.
          '[&_[data-slot=form-message]]:animate-in [&_[data-slot=form-message]]:fade-in-0 [&_[data-slot=form-message]]:duration-150 [&_[data-slot=form-message]]:ease-out',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="portal-dialog-close"
            className="focus-visible:ring-ring/50 absolute top-4 right-4 grid size-7 cursor-pointer place-items-center rounded-md text-neutral-500 transition-[color,background-color,transform] duration-150 ease-out outline-none hover:bg-neutral-300 hover:text-neutral-900 focus-visible:ring-[3px] active:scale-95"
          >
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

function PortalDialogHeader({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="portal-dialog-header"
      className={cn('flex flex-col gap-1.5 pr-8', className)}
      {...props}
    />
  );
}

function PortalDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="portal-dialog-title"
      className={cn(
        'text-[15px] leading-6 font-semibold tracking-[-0.01em]',
        className,
      )}
      {...props}
    />
  );
}

function PortalDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="portal-dialog-description"
      className={cn('text-muted-foreground text-[13px] leading-5', className)}
      {...props}
    />
  );
}

/** Actions, right-aligned above a hairline; stacks on narrow screens. */
function PortalDialogFooter({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="portal-dialog-footer"
      className={cn(
        'flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  );
}

export {
  PortalDialog,
  PortalDialogClose,
  PortalDialogContent,
  PortalDialogDescription,
  PortalDialogFooter,
  PortalDialogHeader,
  PortalDialogTitle,
};
